import { HttpHeaders } from '@angular/common/http';

/**
 * Build the GTW auth + tracing header set that {@link AuthInterceptor}
 * normally attaches to every outbound HttpClient request, so call sites
 * that bypass the interceptor (e.g. the ng2-file-upload XHR used by
 * `<gtw-s3-file-upload>`'s `ap-fileupload` host) can still authenticate
 * against the admin gateway.
 *
 * Mirrors {@link AuthInterceptor.intercept} (`core/http/auth.interceptor.ts`
 * L48-L83):
 *   - `Authorization` from `sessionStorage['Authorization']` (skipped for
 *     gtw-websockets requests).
 *   - `client_key` from `sessionStorage['client_key']`.
 *   - `X-Requested-With: XMLHttpRequest`.
 *   - `ist-req-tkn: <30-char random>` (per-request tracing token).
 *   - `X-XSRF-TOKEN` from the `XSRF-TOKEN` cookie when this is treated as
 *     a state-changing request (default: `true`, the XHR upload case).
 *
 * Returns a fresh {@link HttpHeaders} so the caller can pass it directly
 * to `HttpClient` or enumerate via `.keys()` / `.get()` to build a
 * `{name, value}[]` array for ng2-file-upload's `headers` option.
 *
 * @param opts.includeXsrf  When `false`, omit `X-XSRF-TOKEN` (legacy
 *                          interceptor only adds it for non-GET/non-HEAD).
 *                          Default `true` because the primary consumer is
 *                          a multipart POST.
 * @param opts.url          Optional request URL — if it contains
 *                          `gtw-websockets`, `Authorization` is skipped
 *                          (parity with the interceptor's websocket
 *                          carve-out).
 */
export function buildGtwAuthHeaders(opts: {
  includeXsrf?: boolean;
  url?: string;
} = {}): HttpHeaders {
  const includeXsrf = opts.includeXsrf !== false;
  const url = opts.url || '';

  let headers = new HttpHeaders();

  const authorization = sessionStorage.getItem('Authorization') || '';
  if (authorization && !url.includes('gtw-websockets')) {
    headers = headers.set('Authorization', authorization);
  }

  const clientKey = sessionStorage.getItem('client_key') || '';
  if (clientKey) {
    headers = headers.set('client_key', clientKey);
  }

  headers = headers
    .set('X-Requested-With', 'XMLHttpRequest')
    .set('ist-req-tkn', generateRandomString(30));

  if (includeXsrf) {
    const xsrfToken = getCookie('XSRF-TOKEN');
    if (xsrfToken) {
      headers = headers.set('X-XSRF-TOKEN', xsrfToken);
    }
  }

  return headers;
}

/**
 * Cryptographically-random alphanumeric string of length `length`.
 * Mirrors {@link AuthInterceptor.generateRandomString}.
 */
function generateRandomString(length: number): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[array[i] % chars.length];
  }
  return result;
}

/**
 * Read a cookie by name. Mirrors {@link AuthInterceptor.getCookie}.
 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(^|;\\s*)' + name + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[2]) : null;
}
